import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import HomeBanner from '../Components/HomeBanner';
import MainNav from '../Components/MainNav';
import { Container, Row, Col, Card } from 'react-bootstrap';
import Footer from '../Components/Footer';
import FeedbackSummary from '../Components/FeedbackSummary';
import axios from 'axios';
import config from '../config';

// Authentic Images
import badshahiView from "../Images/view-of-badshahi-mosque.jpg";
import recipiImg from "../Images/recipi.jpg";
import bbqImg from "../Images/bbq.jpeg";
import dish3Img from "../Images/dish-3.jpeg";
import dish4Img from "../Images/dish-4.jpeg";
import rooftopImg from "../Images/am.jpg";
import interiorImg from "../Images/inter.jpg";
import gardenImg from "../Images/garden3.jpeg";
import baroodImg from "../Images/barod2.jpeg";

const HIGHLIGHTS = [
  { value: '25+', label: 'Years of Heritage' },
  { value: '9', label: 'Unique Dining Spaces' },
  { value: '500+', label: 'Events Hosted' },
];

const SIGNATURE_DISHES = [
  {
    id: 1,
    title: 'Haveli Special Mutton Karahi',
    tag: "Chef's Signature",
    desc: 'Slow-cooked in an authentic iron wok with fresh tomatoes, green chilies, hand-crushed spices & aromatic ginger juliennes.',
    portion: 'Full (Serves 3-4)',
    img: recipiImg,
    badgeColor: 'gold',
  },
  {
    id: 2,
    title: 'Royal Tandoori Lamb Chops',
    tag: 'Must Try',
    desc: 'Tender baby lamb chops marinated for 24 hours in rich Lahori spices and roasted to smoky perfection over live charcoal.',
    portion: '6 Pieces',
    img: bbqImg,
    badgeColor: 'crimson',
  },
  {
    id: 3,
    title: 'Shahi Dum Pukht Biryani',
    tag: 'Royal Heritage',
    desc: 'Aromatic aged basmati sealed with puff pastry, infused with saffron strands, rose water, kewra, and melt-in-mouth lamb.',
    portion: 'Serves 2-3',
    img: dish3Img,
    badgeColor: 'gold',
  },
  {
    id: 4,
    title: 'Barood Khana BBQ Platter',
    tag: 'Grand Feast',
    desc: 'An imperial spread featuring Reshmi Kebabs, Mutton Boti, Malai Tikka, Fish Tikka & fresh Tandoori Roghani Naans.',
    portion: 'Serves 4',
    img: dish4Img,
    badgeColor: 'crimson',
  },
];

const REVIEWS = [
  {
    name: 'Mark & Sarah Jenkins',
    role: 'Travel Writers, UK',
    text: 'Sitting on Haveli’s rooftop at sunset with the majestic Badshahi Mosque lit up right in front of us was the highlight of our entire trip to Pakistan. The Mutton Karahi is hands down the best we’ve ever tasted!',
    rating: 5,
    tag: 'Rooftop Experience',
  },
  {
    name: 'Syed Hamza Ali',
    role: 'Food Connoisseur, Lahore',
    text: 'Haveli is the undisputed crown jewel of Food Street. The ambiance in the Mughal rooms is royalty personified, and the warmth of their hospitality makes every family dinner feel like a festive celebration.',
    rating: 5,
    tag: 'Heritage & Hospitality',
  },
  {
    name: 'Dr. Ayesha Tariq',
    role: 'Corporate Guest',
    text: 'We hosted our international delegation at Haveli’s Barood Khana hall. Our guests were mesmerized by the 18th-century architecture, live traditional music, and exquisite BBQ platter.',
    rating: 5,
    tag: 'VIP Banquet',
  },
];

function Home() {
  const [restaurantData, setRestaurantData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios
      .get(`${config.API_BASE_URL}/api/home`)
      .then((response) => {
        setRestaurantData(response.data);
        setError(false);
      })
      .catch((err) => {
        console.error('Error fetching data:', err);
        setError(true);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="home-page-wrapper luxury-dark-page">
        <MainNav />
        <div className="haveli-page-loader luxury-loader">
          <div className="luxury-loader__crest">⚜</div>
          <div className="haveli-page-loader__ring" />
          <p className="haveli-page-loader__text">Haveli Restaurant</p>
          <span className="haveli-page-loader__sub">Unveiling The Royal Experience…</span>
        </div>
      </div>
    );
  }

  const headingName = restaurantData?.heading_name || 'Living Heritage of Lahore';

  return (
    <div className="home-page-wrapper luxury-dark-page">
      <MainNav />
      <HomeBanner />

      {/* HERITAGE STORY SECTION */}
      <section id="welcome" className="luxury-story-section">
        <div className="ambient-radial-glow" />
        <Container>
          <Row className="align-items-center g-5">
            <Col lg={6}>
              <div className="luxury-story-visual">
                <div className="arch-frame-container">
                  <div 
                    className="arch-frame-image"
                    style={{ backgroundImage: `url(${badshahiView})` }}
                  />
                  <div className="arch-frame-overlay" />
                  <div className="arch-frame-border" />
                </div>
                
                {/* Floating Heritage Seal */}
                <div className="floating-heritage-seal">
                  <span className="seal-crest">⚜</span>
                  <span className="seal-num">25+</span>
                  <span className="seal-text">Years of Grandeur</span>
                </div>

                {/* Bottom Tag */}
                <div className="floating-location-tag">
                  <span className="loc-dot" />
                  <span>Haveli Khalil Khan, Fort Road Food Street</span>
                </div>
              </div>
            </Col>

            <Col lg={6}>
              <div className="luxury-story-content">
                <div className="luxury-pill-badge">
                  <span>✦ LIVING HERITAGE OF LAHORE ✦</span>
                </div>

                <h2 className="luxury-story-title">{headingName}</h2>

                <div className="luxury-gold-line">
                  <span className="gold-sparkle">◆</span>
                </div>

                <p className="luxury-story-lead">
                  Perched high above the legendary Walled City, Haveli Restaurant is not merely a dining venue — it is a living, breathing testament to the golden era of Mughal Lahore.
                </p>

                <p className="luxury-story-desc">
                  Lovingly restored by brothers Habib Khan and Tariq Khan from the historic 18th-century Haveli Khalil Khan, our halls feature original sun-dried bricks, hand-carved woodwork, and wrought-iron balconies.
                </p>

                <p className="luxury-story-desc">
                  Dine while gazing at the grand illuminated domes of Badshahi Mosque, the historic Minar-e-Pakistan, and Lahore Fort, while savouring authentic family recipes passed down through generations.
                </p>

                <div className="story-highlights-row">
                  <div className="story-mini-badge">
                    <span className="badge-icon">👑</span>
                    <span className="badge-title">Imperial Atmosphere</span>
                  </div>
                  <div className="story-mini-badge">
                    <span className="badge-icon">🥘</span>
                    <span className="badge-title">Authentic Spices</span>
                  </div>
                  <div className="story-mini-badge">
                    <span className="badge-icon">✨</span>
                    <span className="badge-title">Iconic Night Vistas</span>
                  </div>
                </div>

                <div className="luxury-story-cta">
                  <Link to="/our-history" className="luxury-btn-primary">
                    <span>Explore Our 300-Year History</span>
                    <span className="arrow-shift">→</span>
                  </Link>
                  <Link to="/dining-experience" className="luxury-btn-ghost">
                    <span>View Dining Areas</span>
                  </Link>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* 3. ROYAL STATS RIBBON */}
      <section className="luxury-stats-ribbon" aria-label="Key Milestones">
        <Container>
          <div className="luxury-stats-wrapper">
            <Row className="g-4 text-center justify-content-center">
              {HIGHLIGHTS.map((item) => (
                <Col xs={4} md={4} key={item.label}>
                  <div className="luxury-stat-box">
                    <span className="luxury-stat-number">{item.value}</span>
                    <span className="luxury-stat-title">{item.label}</span>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        </Container>
      </section>

      {/* 4. IMPERIAL DELICACIES (SIGNATURE FOOD SHOWCASE) */}
      <section className="luxury-dishes-section">
        <Container>
          <div className="text-center luxury-section-header">
            <div className="luxury-pill-badge luxury-pill-badge--gold">
              <span>⚜ MASTERPIECE CUISINE ⚜</span>
            </div>
            <h2 className="luxury-section-title">Chef&apos;s Imperial Delicacies</h2>
            <p className="luxury-section-subtitle">
              Prepared using traditional clay ovens, iron karahis, and secret spice blends dating back to imperial Mughal banquets.
            </p>
          </div>

          <Row className="g-4">
            {SIGNATURE_DISHES.map((dish) => (
              <Col lg={3} md={6} key={dish.id}>
                <div className="luxury-dish-card">
                  <div className="dish-card__image-wrap">
                    <div 
                      className="dish-card__image"
                      style={{ backgroundImage: `url(${dish.img})` }}
                    />
                    <div className="dish-card__gradient" />
                    <span className={`dish-card__badge badge--${dish.badgeColor}`}>
                      {dish.tag}
                    </span>
                    <span className="dish-card__portion">{dish.portion}</span>
                  </div>

                  <div className="dish-card__body">
                    <div className="dish-card__top">
                      <h3 className="dish-card__title">{dish.title}</h3>
                    </div>

                    <p className="dish-card__desc">{dish.desc}</p>
                  </div>
                </div>
              </Col>
            ))}
          </Row>

          <div className="text-center mt-5">
            <Link to="/menu" className="luxury-menu-btn">
              <span>View Complete 2026 Dining Menu</span>
              <span className="btn-sparkle">✦</span>
            </Link>
          </div>
        </Container>
      </section>

      {/* 5. ARCHITECTURAL SPACES BENTO SHOWCASE */}
      <section className="luxury-spaces-section">
        <Container>
          <div className="text-center luxury-section-header">
            <div className="luxury-pill-badge">
              <span>✦ UNRIVALLED ATMOSPHERE ✦</span>
            </div>
            <h2 className="luxury-section-title">Imperial Dining Spaces</h2>
            <p className="luxury-section-subtitle">
              Choose your ideal setting — from rooftop dining under the stars to private royal chambers.
            </p>
          </div>

          <div className="spaces-bento-grid">
            {/* Bento Item 1: Featured Rooftop (Large) */}
            <div className="bento-card bento-card--large">
              <div 
                className="bento-card__bg"
                style={{ backgroundImage: `url(${rooftopImg})` }}
              />
              <div className="bento-card__overlay" />
              <div className="bento-card__content">
                <span className="bento-badge">Most Iconic View</span>
                <h3 className="bento-title">Open Sky Rooftop</h3>
                <p className="bento-desc">
                  Direct panoramic view of Badshahi Mosque, Minar-e-Pakistan, and the illuminated Food Street under the starry Lahore sky.
                </p>
                <Link to="/open-sky" className="bento-link">
                  <span>Explore Rooftop Dining →</span>
                </Link>
              </div>
            </div>

            {/* Bento Item 2: Mughal Chambers */}
            <div className="bento-card bento-card--medium">
              <div 
                className="bento-card__bg"
                style={{ backgroundImage: `url(${interiorImg})` }}
              />
              <div className="bento-card__overlay" />
              <div className="bento-card__content">
                <span className="bento-badge">Royal Heritage</span>
                <h3 className="bento-title">Mughal Chambers</h3>
                <p className="bento-desc">
                  Air-conditioned royal dining halls with hand-carved woodwork, velvet upholstery &amp; antique paintings.
                </p>
                <Link to="/dining-experience" className="bento-link">
                  <span>View Chambers →</span>
                </Link>
              </div>
            </div>

            {/* Bento Item 3: Haveli Courtyard Garden */}
            <div className="bento-card bento-card--medium">
              <div 
                className="bento-card__bg"
                style={{ backgroundImage: `url(${gardenImg})` }}
              />
              <div className="bento-card__overlay" />
              <div className="bento-card__content">
                <span className="bento-badge">Tranquil Serenity</span>
                <h3 className="bento-title">Haveli Garden</h3>
                <p className="bento-desc">
                  Lush courtyard greenery, warm lantern illumination, and peaceful open-air dining.
                </p>
                <Link to="/haveligarden" className="bento-link">
                  <span>Discover Garden →</span>
                </Link>
              </div>
            </div>

            {/* Bento Item 4: Barood Khana Hall */}
            <div className="bento-card bento-card--wide">
              <div 
                className="bento-card__bg"
                style={{ backgroundImage: `url(${baroodImg})` }}
              />
              <div className="bento-card__overlay" />
              <div className="bento-card__content">
                <span className="bento-badge">Historic 18th Century Armory</span>
                <h3 className="bento-title">Barood Khana Imperial Hall</h3>
                <p className="bento-desc">
                  An authentic historical landmark transformed into Lahore’s most distinguished banquet hall for private galas, Nikkah ceremonies, and VIP gatherings.
                </p>
                <Link to="/baroodkhana" className="bento-link">
                  <span>Explore Barood Khana →</span>
                </Link>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* 6. VIP GUEST REVIEWS & ACCOLADES */}
      <section className="luxury-reviews-section">
        <Container>
          <div className="text-center luxury-section-header">
            <div className="luxury-pill-badge luxury-pill-badge--gold">
              <span>⚜ GUEST REPUTATION ⚜</span>
            </div>
            <h2 className="luxury-section-title">Loved by Food Lovers Across the Globe</h2>
            <p className="luxury-section-subtitle">
              A dining experience cherished by international travellers, heads of state, and generations of Lahoris.
            </p>
          </div>

          <FeedbackSummary />

          <Row className="g-4">
            {REVIEWS.map((review, idx) => (
              <Col lg={4} md={6} key={idx}>
                <div className="luxury-review-card">
                  <div className="review-stars">
                    {'★'.repeat(review.rating)}
                  </div>
                  <span className="review-tag">{review.tag}</span>
                  <p className="review-text">&ldquo;{review.text}&rdquo;</p>
                  <div className="review-author">
                    <span className="author-name">{review.name}</span>
                    <span className="author-role">{review.role}</span>
                  </div>
                  <div className="review-quote-mark">“</div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* 7. ROYAL CELEBRATION PAVILION CTA */}
      <section className="luxury-cta-section">
        <Container>
          <div className="luxury-cta-pavilion">
            <div className="pavilion-glow" />
            <div className="pavilion-content">
              <div className="luxury-pill-badge luxury-pill-badge--gold">
                <span>⚜ HOST YOUR ROYAL OCCASION ⚜</span>
              </div>
              <h2 className="pavilion-title">Make Your Next Celebration Unforgettable</h2>
              <p className="pavilion-desc">
                From romantic rooftop candlelit dinners to royal Nikkah ceremonies, birthday banquets, and corporate galas — our master hospitality team crafts every single detail to perfection.
              </p>
              <div className="pavilion-actions">
                <Link to="/contact" className="pavilion-btn-primary">
                  <span>Reserve a Table</span>
                  <span className="btn-arrow">→</span>
                </Link>
                <Link to="/catering" className="pavilion-btn-outline">
                  <span>Weddings &amp; Catering Information</span>
                  <span className="btn-sparkle">✦</span>
                </Link>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {error && (
        <p className="home-api-notice text-center text-muted small py-2">
          Live content preview active.
        </p>
      )}

      <Footer />
    </div>
  );
}

export default Home;
